import React from 'react';
import { Shield, CheckCircle, Users, Award, Phone } from 'lucide-react';

const SafetyStandardsPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="relative h-96 overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/r9xsk5p6_Safety%20standards.png"
          alt="Safety Standards"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/56 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <h1 className="text-6xl md:text-8xl font-black text-white uppercase tracking-tight mb-4">
              Safety <span className="text-yellow-400">Standards</span>
            </h1>
            <p className="text-xl text-gray-300">World-Class Safety Protocols</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Main Section */}
            <section className="bg-gradient-to-br from-yellow-400/10 to-yellow-900/10 border border-yellow-400/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Shield className="text-yellow-400 flex-shrink-0 mt-1" size={48} />
                <div>
                  <h2 className="text-3xl font-bold text-white mb-4">Our Safety Standards</h2>
                </div>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
                <p>
                  We have an experienced and energetic young crew ensuring every <strong className="text-white">SOP (Standard Operating Procedure)</strong> is strictly followed before each jump. As India doesn't have its own SOPs for the sport, we follow <strong className="text-white">SANZ (Standards of Australia and New Zealand)</strong> guidelines to maintain world-class safety.
                </p>
                <p>
                  All our equipment is certified and of high quality. Our Jump Directors check all safety parameters before every jump, making Indya Bungy India's best bungee jumping destination for a safe and thrilling bungee jumping experience in India and one of the highest bungee jumps in India.
                </p>
              </div>
            </section>

            {/* Key Safety Features */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Key Safety Features</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="text-white font-bold mb-1">SANZ Compliance</h4>
                      <p className="text-gray-300 text-sm">Following Australian & New Zealand standards for all operations</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="text-white font-bold mb-1">Certified Equipment</h4>
                      <p className="text-gray-300 text-sm">All equipment is internationally certified and regularly inspected</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="text-white font-bold mb-1">Pre-Jump Checks</h4>
                      <p className="text-gray-300 text-sm">Comprehensive safety checks before every single jump</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="text-white font-bold mb-1">Experienced Crew</h4>
                      <p className="text-gray-300 text-sm">Young, energetic team with extensive training and experience</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="text-white font-bold mb-1">Jump Directors</h4>
                      <p className="text-gray-300 text-sm">Expert jump directors verify all safety parameters</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h4 className="text-white font-bold mb-1">Strict SOPs</h4>
                      <p className="text-gray-300 text-sm">Every procedure followed exactly as per international standards</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Safety Process */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Our Safety Process</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Medical Screening</h4>
                    <p className="text-gray-300 text-sm">Comprehensive health check and medical declaration</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Equipment Selection</h4>
                    <p className="text-gray-300 text-sm">Weight-based equipment selection and verification</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Safety Briefing</h4>
                    <p className="text-gray-300 text-sm">Detailed instructions and safety procedures explained</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Equipment Fitting</h4>
                    <p className="text-gray-300 text-sm">Professional harness fitting and double-checking</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold">5</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Final Verification</h4>
                    <p className="text-gray-300 text-sm">Jump Director performs final safety check before jump</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Images Grid */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1565688534245-05d6b5be184a?w=400&q=80"
                  alt="Safety Standards 1"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&q=80"
                  alt="Safety Standards 2"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1569025690938-a00729c9e1f9?w=400&q=80"
                  alt="Safety Standards 3"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>

            {/* Safety Record */}
            <section className="bg-gradient-to-br from-green-500/10 to-green-900/10 border border-green-500/20 rounded-xl p-8 text-center">
              <div className="text-7xl font-black text-green-400 mb-4">100%</div>
              <h3 className="text-2xl font-bold text-white mb-2">Perfect Safety Record</h3>
              <p className="text-gray-300">
                With over 44,000 jumps completed, we maintain a flawless safety record with zero major incidents.
              </p>
            </section>

            {/* CTA */}
            <section className="text-center">
              <a
                href="tel:8506865393"
                className="inline-flex items-center space-x-3 bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
              >
                <Phone size={24} />
                <span>Call Us: 8506865393</span>
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyStandardsPage;
